import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { usePopper } from "react-popper";
import { type Placement } from "@popperjs/core";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

interface Props {
  children?: ReactNode;
  placement?: Placement;
  trigger: ReactNode;
  container?: FC;
  delay?: number;
  className?: string;
  disabled?: boolean;
  triggerContainerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const Container: FC<{ className?: string; children?: ReactNode }> = ({
  className,
  children,
  ...rest
}) => {
  return (
    <div className={`${styles.tooltip} ${className}`} {...rest}>
      {children}
    </div>
  );
};

const Tooltip: FC<Props> = ({
  placement = "bottom",
  trigger,
  className,
  container,
  children,
  delay = 0,
  disabled = false,
  triggerContainerClassName,
  open,
  onOpenChange,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles: pooperStyles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
    }
  );
  const portalRef = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (open !== undefined) {
      return;
    }

    const onMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, delay);
    };

    const onMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShowTooltip(false);
      onOpenChange?.(false);
    };

    if (referenceElement) {
      referenceElement.addEventListener("mouseenter", onMouseEnter);
      referenceElement.addEventListener("mouseleave", onMouseLeave);
      referenceElement.addEventListener("click", onMouseLeave);
    }
    return () => {
      if (referenceElement) {
        referenceElement.removeEventListener("mouseenter", onMouseEnter);
        referenceElement.removeEventListener("mouseleave", onMouseLeave);
        referenceElement.removeEventListener("click", onMouseLeave);
      }
    };
  }, [delay, referenceElement, open, onOpenChange]);

  useEffect(() => {
    if (open !== undefined && open && popperElement) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          referenceElement &&
          popperElement &&
          !referenceElement.contains(event.target as Node) &&
          !popperElement.contains(event.target as Node)
        ) {
          onOpenChange?.(false);
        }
      };

      const handleScroll = () => {
        onOpenChange?.(false);
      };

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
    return undefined;
  }, [open, popperElement, referenceElement, onOpenChange]);

  useEffect(() => {
    portalRef.current = document.getElementById("tooltip-portal");
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  return (
    <>
      <div
        className={`flex ${triggerContainerClassName}`}
        ref={setReferenceElement}
      >
        {trigger}
      </div>
      {mounted && portalRef.current && !disabled
        ? createPortal(
            <>
              {(open !== undefined ? open : showTooltip) ? (
                <div
                  role="tooltip"
                  ref={setPopperElement}
                  style={{ ...pooperStyles.popper, zIndex: 300000 }}
                  {...attributes.popper}
                >
                  {!container ? (
                    <Container className={className}>{children}</Container>
                  ) : (
                    container({ children })
                  )}
                </div>
              ) : null}
            </>,
            portalRef.current
          )
        : null}
    </>
  );
};

export default Tooltip;
